import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  ShoppingCart,
  ArrowLeft,
  Globe,
  Webhook,
  ExternalLink,
  XCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  Search,
  ChevronDown,
  Star,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CreateSaleShopData, Currency } from '@/types'
import { saleShopService } from '@/services/saleShopService'
import { helpersService } from '@/services/helpersService'
export default function CreateSaleShop() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [popularCurrencies, setPopularCurrencies] = useState<Currency[]>([])
  const [currencySearchQuery, setCurrencySearchQuery] = useState('')
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false)
  const [currencyLoading, setCurrencyLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const currencyDropdownRef = useRef<HTMLDivElement>(null)
  const [formData, setFormData] = useState<CreateSaleShopData>({
    name: '',
    description: '',
    website_url: '',
    webhook_url: '',
    redirect_url: '',
    cancel_url: '',
    currency_id: undefined,
    is_active: true,
  })

  useEffect(() => {
    fetchCurrencies()
    fetchPopularCurrencies()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCurrencyDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const fetchCurrencies = async () => {
    try {
      const data = await helpersService.getCurrencies()
      setCurrencies(data)
    } catch (error) {
      console.error('Failed to fetch currencies:', error)
    }
  }

  const fetchPopularCurrencies = async () => {
    try {
      const data = await helpersService.getPopularCurrencies()
      setPopularCurrencies(data)
    } catch (error) {
      console.error('Failed to fetch popular currencies:', error)
    }
  }

  const searchCurrencies = async (query: string) => {
    if (!query.trim()) {
      setCurrencies([])
      return
    }

    setCurrencyLoading(true)
    try {
      const data = await helpersService.searchCurrencies(query)
      setCurrencies(data)
    } catch (error) {
      console.error('Failed to search currencies:', error)
    } finally {
      setCurrencyLoading(false)
    }
  }

  const handleCurrencySearch = (query: string) => {
    setCurrencySearchQuery(query)
    if (query.length >= 2) {
      searchCurrencies(query)
    } else if (query.length === 0) {
      fetchCurrencies()
    }
  }

  const selectCurrency = (currency: Currency) => {
    setFormData(prev => ({
      ...prev,
      currency_id: currency.id,
    }))
    setCurrencySearchQuery(`${currency.code} - ${currency.name}`)
    setShowCurrencyDropdown(false)
  }

  const handleInputChange = (field: keyof CreateSaleShopData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Sale shop name is required'
    }

    if (!formData.currency_id) {
      newErrors.currency_id = 'Please select a currency'
    }

    if (formData.website_url && !isValidUrl(formData.website_url)) {
      newErrors.website_url = 'Website URL must be a valid URL starting with http:// or https://'
    }

    if (formData.webhook_url && !isValidUrl(formData.webhook_url)) {
      newErrors.webhook_url = 'Webhook URL must be a valid URL starting with http:// or https://'
    }

    if (formData.redirect_url && !isValidUrl(formData.redirect_url)) {
      newErrors.redirect_url = 'Redirect URL must be a valid URL starting with http:// or https://'
    }

    if (formData.cancel_url && !isValidUrl(formData.cancel_url)) {
      newErrors.cancel_url = 'Cancel URL must be a valid URL starting with http:// or https://'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      const response = await saleShopService.createSaleShop(formData)

      if (response.success) {
        navigate(`/sale-shops/${response.data.id}`)
      }
    } catch (error: any) {
      const errorMessage = saleShopService.handleError(error)
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-6 space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/sale-shops')}
              className="p-2 text-white rounded-lg hover:bg-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1
                className="text-4xl font-bold"
                style={pageStyles.text}
              >
                Create Sales Channel
              </h1>
              <p
                className="text-lg"
                style={pageStyles.textSecondary}
              >
                Set up a new sales channel for your business
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="mx-auto ">
          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-8 card-content">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* General Error */}
                {errors.general && (
                  <div
                    className="flex items-center p-4 space-x-3 border rounded-lg"
                    style={{
                      backgroundColor: statusColors.error.backgroundColor,
                      borderColor: statusColors.error.borderColor
                    }}
                  >
                    <AlertCircle
                      className="flex-shrink-0 w-5 h-5"
                      style={{ color: statusColors.error.color }}
                    />
                    <p style={{ color: statusColors.error.color }}>
                      {errors.general}
                    </p>
                  </div>
                )}

                {/* Basic Information */}
                <div className="space-y-6">
                  <div className="pb-4">
                    <h2
                      className="flex items-center space-x-3 text-2xl font-semibold"
                      style={pageStyles.text}
                    >
                      <div
                        className="p-2 rounded-lg"
                        style={{
                          backgroundColor: `${branding.primaryColor}20`
                        }}
                      >
                        <ShoppingCart
                          className="w-6 h-6"
                          style={{ color: branding.primaryColor }}
                        />
                      </div>
                      <span>Basic Information</span>
                    </h2>
                    <p
                      className="mt-2"
                      style={pageStyles.textSecondary}
                    >
                      Enter the basic details for your sales channel
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <label
                        className="block mb-3 text-sm font-semibold"
                        style={pageStyles.textSecondary}
                      >
                        Channel Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={e => handleInputChange('name', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.name
                          ? 'border-orange-500 focus:ring-orange-500'
                          : 'hover:border-gray-500'
                          }`}
                        style={{
                          backgroundColor: `${branding.surfaceColor}80`,
                          borderColor: errors.name ? statusColors.error.borderColor : `${branding.textSecondaryColor}30`,
                          color: branding.textColor,
                        }}
                        placeholder="Enter your sales channel name"
                      />
                      {errors.name && (
                        <p
                          className="flex items-center mt-2 space-x-1 text-sm"
                          style={{ color: statusColors.error.color }}
                        >
                          <XCircle className="w-4 h-4" />
                          <span>{errors.name}</span>
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label
                        className="block mb-3 text-sm font-semibold"
                        style={pageStyles.textSecondary}
                      >
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={e => handleInputChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                        style={{
                          backgroundColor: `${branding.surfaceColor}80`,
                          borderColor: `${branding.textSecondaryColor}30`,
                          color: branding.textColor,
                        }}
                        placeholder="Describe your sales channel (optional)"
                      />
                    </div>

                    <div>
                      <label
                        className="block mb-3 text-sm font-semibold"
                        style={pageStyles.textSecondary}
                      >
                        Currency *
                      </label>
                      <div className="relative" ref={currencyDropdownRef}>
                        <div className="relative">
                          <Search
                            className="absolute w-5 h-5 transform -translate-y-1/2 left-4 top-1/2"
                            style={{ color: branding.textSecondaryColor }}
                          />
                          <input
                            type="text"
                            value={currencySearchQuery}
                            onChange={e => handleCurrencySearch(e.target.value)}
                            onFocus={() => setShowCurrencyDropdown(true)}
                            placeholder="Search currencies (e.g., USD, Euro, Dollar)"
                            className="w-full px-4 py-3 pl-12 pr-12 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                            style={{
                              backgroundColor: `${branding.surfaceColor}80`,
                              borderColor: `${branding.textSecondaryColor}30`,
                              color: branding.textColor,
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                            className="absolute transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                            style={{ color: branding.textSecondaryColor }}
                          >
                            <ChevronDown
                              className={`h-5 w-5 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </div>

                        {showCurrencyDropdown && (
                          <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg max-h-64">
                            {currencySearchQuery.length === 0 && popularCurrencies.length > 0 && (
                              <div className="p-3 border-b border-gray-100">
                                <div className="flex items-center space-x-2 text-sm font-medium text-gray-600">
                                  <Star className="w-4 h-4 text-yellow-500" />
                                  <span>Popular Currencies</span>
                                </div>
                              </div>
                            )}

                            {currencySearchQuery.length === 0 && popularCurrencies.length > 0 && (
                              <div className="overflow-y-auto max-h-32">
                                {popularCurrencies.map(currency => (
                                  <button
                                    key={currency.id}
                                    type="button"
                                    onClick={() => selectCurrency(currency)}
                                    className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50"
                                  >
                                    <div>
                                      <div className="font-medium text-gray-900">
                                        {currency.code} - {currency.name}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {currency.symbol} • {currency.decimal_places} decimal places
                                      </div>
                                    </div>
                                    <Star className="w-4 h-4 text-yellow-500" />
                                  </button>
                                ))}
                              </div>
                            )}

                            {currencySearchQuery.length > 0 && (
                              <div className="p-3 border-b border-gray-100">
                                <div className="text-sm font-medium text-gray-600">
                                  Search Results
                                </div>
                              </div>
                            )}

                            <div className="overflow-y-auto max-h-32">
                              {currencyLoading ? (
                                <div className="p-4 text-center">
                                  <Loader2 className="w-5 h-5 mx-auto text-gray-400 animate-spin" />
                                  <p className="mt-2 text-sm text-gray-500">
                                    Searching currencies...
                                  </p>
                                </div>
                              ) : currencies.length > 0 ? (
                                currencies.map(currency => (
                                  <button
                                    key={currency.id}
                                    type="button"
                                    onClick={() => selectCurrency(currency)}
                                    className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50"
                                  >
                                    <div>
                                      <div className="font-medium text-gray-900">
                                        {currency.code} - {currency.name}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {currency.symbol} • {currency.decimal_places} decimal places
                                      </div>
                                    </div>
                                  </button>
                                ))
                              ) : currencySearchQuery.length > 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                  <p className="text-sm">
                                    No currencies found for "{currencySearchQuery}"
                                  </p>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>


                      {errors.currency_id && (
                        <p
                          className="flex items-center mt-2 space-x-1 text-sm"
                          style={{ color: statusColors.error.color }}
                        >
                          <XCircle className="w-4 h-4" />
                          <span>{errors.currency_id}</span>
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-3 cursor-pointer group">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={e => handleInputChange('is_active', e.target.checked)}
                            className="sr-only"
                          />
                          <div
                            className="w-5 h-5 border-2 rounded-md transition-all duration-200 flex items-center justify-center"
                            style={{
                              backgroundColor: formData.is_active ? branding.primaryColor : `${branding.surfaceColor}80`,
                              borderColor: formData.is_active ? branding.primaryColor : branding.textSecondaryColor,
                              boxShadow: formData.is_active ? `0 10px 15px -3px ${branding.primaryColor}25` : 'none'
                            }}
                          >
                            {formData.is_active && (
                              <CheckCircle
                                className="w-3 h-3"
                                style={{ color: branding.textColor }}
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <span
                            className="text-sm font-semibold transition-colors"
                            style={{ color: branding.textSecondaryColor }}
                          >
                            Active Channel
                          </span>
                          <span
                            className="text-xs"
                            style={{ color: branding.textSecondaryColor }}
                          >
                            {formData.is_active ? 'Channel is enabled' : 'Channel is disabled'}
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* URLs Configuration */}
                <div className="space-y-6">
                  <div className="pb-4 border-b border-gray-700">
                    <h2
                      className="flex items-center space-x-3 text-2xl font-semibold"
                      style={pageStyles.text}
                    >
                      <div
                        className="p-2 rounded-lg"
                        style={{
                          backgroundColor: `${branding.primaryColor}20`
                        }}
                      >
                        <Globe
                          className="w-6 h-6"
                          style={{ color: branding.primaryColor }}
                        />
                      </div>
                      <span>URLs Configuration</span>
                    </h2>
                    <p
                      className="mt-2"
                      style={pageStyles.textSecondary}
                    >
                      Configure URLs for your sales channel (all optional)
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <label
                        className="block mb-3 text-sm font-semibold"
                        style={pageStyles.textSecondary}
                      >
                        Website URL
                      </label>
                      <div className="relative">
                        <Globe
                          className="absolute w-5 h-5 transform -translate-y-1/2 left-4 top-1/2"
                          style={{ color: branding.textSecondaryColor }}
                        />
                        <input
                          type="url"
                          value={formData.website_url}
                          onChange={e => handleInputChange('website_url', e.target.value)}
                          className={`w-full px-4 py-3 pl-12 pr-4 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.website_url
                            ? 'border-orange-500 focus:ring-orange-500'
                            : 'hover:border-gray-500'
                            }`}
                          style={{
                            backgroundColor: `${branding.surfaceColor}80`,
                            borderColor: errors.website_url ? statusColors.error.borderColor : `${branding.textSecondaryColor}30`,
                            color: branding.textColor,
                          }}
                          placeholder="https://yourwebsite.com"
                        />
                      </div>
                      {errors.website_url && (
                        <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                          <XCircle className="w-4 h-4" />
                          <span>{errors.website_url}</span>
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block mb-3 text-sm font-semibold text-gray-300">
                        Webhook URL
                      </label>
                      <div className="relative">
                        <Webhook className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                        <input
                          type="url"
                          value={formData.webhook_url}
                          onChange={e => handleInputChange('webhook_url', e.target.value)}
                          className={`w-full px-4 py-3 pl-12 pr-4 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.webhook_url
                            ? 'border-orange-500 focus:ring-orange-500'
                            : 'hover:border-gray-500'
                            }`}
                          placeholder="https://yourwebsite.com/webhooks"
                        />
                      </div>
                      {errors.webhook_url && (
                        <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                          <XCircle className="w-4 h-4" />
                          <span>{errors.webhook_url}</span>
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block mb-3 text-sm font-semibold text-gray-300">
                        Success Redirect URL
                      </label>
                      <div className="relative">
                        <ExternalLink className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                        <input
                          type="url"
                          value={formData.redirect_url}
                          onChange={e => handleInputChange('redirect_url', e.target.value)}
                          className={`w-full px-4 py-3 pl-12 pr-4 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.redirect_url
                            ? 'border-orange-500 focus:ring-orange-500'
                            : 'hover:border-gray-500'
                            }`}
                          placeholder="https://yourwebsite.com/success"
                        />
                      </div>
                      {errors.redirect_url && (
                        <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                          <XCircle className="w-4 h-4" />
                          <span>{errors.redirect_url}</span>
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block mb-3 text-sm font-semibold text-gray-300">
                        Cancel Redirect URL
                      </label>
                      <div className="relative">
                        <XCircle className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                        <input
                          type="url"
                          value={formData.cancel_url}
                          onChange={e => handleInputChange('cancel_url', e.target.value)}
                          className={`w-full px-4 py-3 pl-12 pr-4 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.cancel_url
                            ? 'border-orange-500 focus:ring-orange-500'
                            : 'hover:border-gray-500'
                            }`}
                          placeholder="https://yourwebsite.com/cancel"
                        />
                      </div>
                      {errors.cancel_url && (
                        <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                          <XCircle className="w-4 h-4" />
                          <span>{errors.cancel_url}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div
                  className="flex flex-col items-center justify-end pt-8 space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4"
                  style={{ borderTopColor: `${branding.textSecondaryColor}30` }}
                >
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/sale-shops')}
                    className="w-full px-8 py-3 sm:w-auto"
                    style={{
                      backgroundColor: 'transparent',
                      borderColor: branding.textSecondaryColor,
                      color: branding.textSecondaryColor
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="w-full px-8 py-3 sm:w-auto"
                    style={{
                      backgroundColor: branding.primaryColor,
                      color: branding.textColor
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Creating Channel...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Create Channel
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
